import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import type { IObserverPosting, IObserverProfile } from '../interface/observer';

/**
 * api.core reads for an independent observer. No PartyId header: observers have no party, and
 * api.core scopes these routes by the token alone.
 */
const observerApi = axios.create({ baseURL: COREAPI, timeout: 15_000 });

observerApi.interceptors.request.use((config) => {
	const { token } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	return config;
});

const dataOf = (body: unknown): unknown => {
	const parsed = typeof body === 'string' ? safeJson(body) : body;
	return parsed && typeof parsed === 'object' && 'data' in parsed ? parsed.data : undefined;
};

const safeJson = (text: string): unknown => {
	try {
		return JSON.parse(text);
	} catch {
		return undefined;
	}
};

/** Every unit the observer is posted to, newest election first. Null when api.core could not answer. */
export const getMyObserverPostings = async (): Promise<IObserverPosting[] | null> => {
	try {
		const res = await observerApi.get('/api/v1/ObserverApplication/me/assignments', { validateStatus: () => true });
		if (res.status !== 200) return null;
		const data = dataOf(res.data);
		return Array.isArray(data) ? (data as IObserverPosting[]).filter((p) => !!p?.entityCode && p.electionId > 0) : null;
	} catch {
		return null;
	}
};

/** The observer's whole profile for Settings › Profile. Null when api.core could not answer. */
export const getMyObserverProfile = async (): Promise<IObserverProfile | null> => {
	try {
		const res = await observerApi.get('/api/v1/ObserverApplication/me/profile', { validateStatus: () => true });
		if (res.status !== 200) return null;
		const data = dataOf(res.data) as Record<string, unknown> | undefined;
		const identity = data?.identity as Record<string, unknown> | undefined;
		if (!identity) return null;
		const name = [identity.firstName, identity.lastName].filter(Boolean).join(' ');
		return {
			credential: {
				fullName: name || getUserData().user?.FullName || '',
				photoUrl: (identity.profilePhotoUrl as string) || null,
				organizationName: (identity.organizationName as string) || null,
				accreditationNumber: (identity.accreditationNumber as string) || null,
				organizationIsAccredited: (identity.organizationIsAccredited as boolean | null) ?? null,
			},
			email: (identity.email as string) || null,
			phoneNumber: (identity.phoneNumber as string) || null,
			deployment: (data?.deployment as IObserverPosting | null) ?? null,
			application: (data?.application as IObserverProfile['application']) ?? null,
			payout: (data?.payout as IObserverProfile['payout']) ?? null,
		};
	} catch {
		return null;
	}
};
